import { useState } from "react";
import axios from "axios";
import { Reveal } from "@/components/site/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "organisation", label: "University / Organisation", type: "text", required: true },
  { name: "contact_name", label: "Contact Name", type: "text", required: true },
  { name: "job_title", label: "Job Title", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "telephone", label: "Telephone", type: "tel", required: true },
  { name: "website", label: "Website", type: "url", required: false },
  { name: "annual_students", label: "Number of annual students", type: "text", required: true },
];

const empty = { organisation: "", contact_name: "", job_title: "", email: "", telephone: "", website: "", annual_students: "", message: "" };

export const EnquiryForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/partnership-enquiry`, form);
      setSent(true);
      setForm(empty);
      toast.success("Enquiry sent", { description: "Thank you — our partnerships team will be in touch shortly." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not send enquiry", { description: typeof detail === "string" ? detail : "Please check your details and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="relative bg-surface py-28 sm:py-32">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <div className="text-center">
          <Reveal><p className="text-xs uppercase tracking-[0.22em] text-cobalt font-semibold">Partnership Enquiry</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display font-extrabold tracking-tighter text-ink text-4xl sm:text-5xl leading-[0.98]">
              Start the conversation.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slatey">
              Tell us about your institution and our partnerships team will be in touch. Enquiries go to{" "}
              <span className="font-semibold text-ink">partnerships@opendegree.co.uk</span>.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {sent ? (
            <div data-testid="enquiry-success" className="mt-10 flex flex-col items-center rounded-3xl border border-emerald-200 bg-white p-12 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-500" />
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-ink">Enquiry received</h3>
              <p className="mt-2 max-w-md text-slatey">Thank you for your interest in partnering with OpenDegree. Our team will respond as soon as possible.</p>
              <button data-testid="enquiry-reset-btn" onClick={() => setSent(false)} className="mt-6 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-surface">
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="enquiry-form" className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <div key={f.name} className={f.name === "organisation" ? "sm:col-span-2" : ""}>
                    <Label htmlFor={f.name} className="text-sm font-medium text-ink">
                      {f.label} {f.required && <span className="text-cobalt">*</span>}
                    </Label>
                    <Input
                      id={f.name}
                      data-testid={`enquiry-${f.name}`}
                      type={f.type}
                      required={f.required}
                      value={form[f.name]}
                      onChange={update(f.name)}
                      className="mt-2 h-11 rounded-xl border-slate-200 bg-white focus-visible:ring-cobalt"
                      placeholder={f.label}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <Label htmlFor="message" className="text-sm font-medium text-ink">Message <span className="text-cobalt">*</span></Label>
                <Textarea
                  id="message"
                  data-testid="enquiry-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="mt-2 rounded-xl border-slate-200 bg-white focus-visible:ring-cobalt"
                  placeholder="Tell us about your institution and what you're looking for…"
                />
              </div>
              <button
                type="submit"
                data-testid="enquiry-submit-btn"
                disabled={loading}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-cobalt px-7 py-4 text-[15px] font-semibold text-white shadow-[0_14px_34px_rgba(16,76,250,0.30)] transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:hover:scale-100"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send Enquiry</>}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
